import { api, tokenManager } from './api';
import { 
  LoginCredentials, 
  LoginResponse, 
  User, 
  ChangePasswordRequest 
} from '@/types/auth.types';

export const authService = {
  // Login user
  login: async (credentials: LoginCredentials): Promise<LoginResponse> => {
    const response = await api.post<any>('/auth/login', credentials);

    if (response.success && response.data) {
      const { token, refresh_token, expires_at, user, remember_me } = response.data;

      // Store tokens with remember_me flag
      tokenManager.setAccessToken(token, remember_me);
      tokenManager.setRefreshToken(refresh_token, remember_me);

      // Calculate expires_in from expires_at timestamp
      const expiresIn = expires_at ? Math.max(0, expires_at - Math.floor(Date.now() / 1000)) : 3600;
      tokenManager.setTokenExpiry(expiresIn, remember_me);

      // Return data in expected format
      return {
        access_token: token,
        refresh_token,
        token_type: 'Bearer',
        expires_in: expiresIn,
        user
      };
    }

    throw new Error(response.error || 'Login failed');
  },

  // Logout user
  logout: async (): Promise<void> => {
    try {
      await api.post('/api/auth/logout');
    } catch (error) {
      // Even if logout fails on server, clear local tokens
      console.warn('Server logout failed:', error);
    } finally {
      tokenManager.clearTokens();
    }
  },

  // Refresh access token
  refreshToken: async (): Promise<string> => {
    const refreshToken = tokenManager.getRefreshToken();
    
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await api.post<any>('/auth/refresh', {
      refresh_token: refreshToken
    });

    if (response.success && response.data) {
      const { token, expires_at } = response.data;
      
      tokenManager.setAccessToken(token);
      
      // Calculate expires_in from expires_at timestamp
      const expiresIn = expires_at ? Math.max(0, expires_at - Math.floor(Date.now() / 1000)) : 3600;
      tokenManager.setTokenExpiry(expiresIn);
      
      return token;
    }

    throw new Error(response.error || 'Token refresh failed');
  },

  // Get current user profile
  getProfile: async (): Promise<User> => {
    const response = await api.get<any>('/api/auth/profile');
    
    if (response.success && response.data) {
      // Backend returns {user: ...} so extract the user
      return response.data.user || response.data;
    }
    
    throw new Error(response.error || 'Failed to fetch profile');
  },

  // Change password
  changePassword: async (data: ChangePasswordRequest): Promise<void> => {
    const response = await api.post('/api/auth/change-password', data);
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to change password');
    }
  },

  // Check if user is authenticated
  isAuthenticated: (): boolean => {
    const token = tokenManager.getAccessToken();
    const expiry = tokenManager.getTokenExpiry();
    
    if (!token || !expiry) {
      return false;
    }
    
    // Check if token is expired
    return expiry > Date.now();
  },

  // Get stored user data
  getCurrentUser: (): User | null => {
    const userData = sessionStorage.getItem('current_user');
    return userData ? JSON.parse(userData) : null;
  },

  // Store user data
  setCurrentUser: (user: User): void => {
    sessionStorage.setItem('current_user', JSON.stringify(user));
  },

  // Clear user data
  clearCurrentUser: (): void => {
    sessionStorage.removeItem('current_user');
  }
};